import { motion } from "framer-motion";
import { ArrowRight, Building2, Mail, MapPin, Phone, FileText, Share2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { KRUMAGO } from "../../../s3-assets/Krumago";
import SocialIcons from "../../../../assets/SocialIcons";
import { isKioskSessionActive } from "../../../../domains/flexOffice/kiosk/kioskSession.js";
import { isNativeMobileApp } from "../../../../utils/googleAuth.js";

const FlexOfficeFooter = ({ company }) => {
    // Kiosk guests reuse this footer unmodified on the booking/my-bookings pages.
    // Social links, site navigation (dead ends outside KIOSK_ALLOWED_PATH_PREFIXES),
    // contact info, and "Powered by" branding aren't relevant on a shared kiosk
    // device — only the legally-required policy links and copyright stay.
    const isKiosk = isKioskSessionActive();
    const [addressLines, setAddressLines] = useState([]);
    const [contactPhone, setContactPhone] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [quickLinks, setQuickLinks] = useState([]);

    const navigate = useNavigate();
    const companyState = useSelector((state) => state.companyReducer.company);
    const companyOperatingLocation = companyState?.operatingLocation?.code;
    const legalDocuments = companyState?.legalDocuments;

    // Function to format contentSubType for display
    const formatContentSubType = (contentSubType) => {
        if (!contentSubType) return "Legal Document";
        return contentSubType
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Handle policy link click
    const handlePolicyClick = (doc, event) => {
        event.preventDefault();
        if (doc.contentSubType) {
            const policyName = doc.contentSubType.toLowerCase();
            navigate(`/policy/${policyName}`);
        }
    };

    // Transform legal documents for quick links
    useEffect(() => {
        if (legalDocuments?.length > 0) {
            const transformedLinks = legalDocuments.map(doc => ({
                name: formatContentSubType(doc.contentSubType) || doc.name || "Legal Document",
                href: "#",
                document: doc,
            }));
            setQuickLinks(transformedLinks);
        } else {
            setQuickLinks([]);
        }
    }, [legalDocuments]);

    // Function to format phone number based on operating location
    const formatPhoneNumber = useCallback((phoneNumber, countryCode = "") => {
        if (!phoneNumber || phoneNumber === "Not Available") return "Not Available";

        const digits = phoneNumber.replace(/\D/g, '');
        if (digits.length < 10) return phoneNumber;

        if (companyOperatingLocation === "US") {
            const areaCode = digits.substring(0, 3);
            const firstPart = digits.substring(3, 6);
            const secondPart = digits.substring(6, 10);
            const code = countryCode || "+1";
            return `${code} (${areaCode}) ${firstPart}-${secondPart}`;
        } else {
            const code = countryCode || (companyOperatingLocation === "IND" ? "+91" : "");
            return code ? `${code} ${digits}` : digits;
        }
    }, [companyOperatingLocation]);

    useEffect(() => {
        const companyData = companyState;

        if (companyData) {
            // Extract address and format into lines
            if (companyData.address) {
                const { streetName1, streetName2, city, state, zipcode, country } = companyData.address;
                const lines = [];

                if (streetName1?.trim()) lines.push(streetName1.trim());
                if (streetName2?.trim()) lines.push(streetName2.trim());

                const cityStateZip = [];
                if (city?.trim()) cityStateZip.push(city.trim());
                if (state?.trim()) cityStateZip.push(state.trim());
                if (zipcode?.trim()) cityStateZip.push(zipcode.trim());

                if (cityStateZip.length > 0) lines.push(cityStateZip.join(", "));
                if (country?.trim()) lines.push(country.trim());

                setAddressLines(lines.length > 0 ? lines : ["Not Available"]);
            } else {
                setAddressLines(["Not Available"]);
            }

            // Extract contact phone and email
            if (companyData.contactPerson) {
                const phone = companyData.contactPerson.phoneNumber || "Not Available";
                const code = companyData.contactPerson.countryCode || "";
                setContactPhone(formatPhoneNumber(phone, code));
                setContactEmail(companyData.contactPerson.email || "Not Available");
            } else {
                setContactPhone("Not Available");
                setContactEmail("Not Available");
            }
        }
    }, [companyState, formatPhoneNumber]);

    // Function to get clean digits for tel: link
    const getCleanPhoneDigits = (formattedPhone) => {
        if (!formattedPhone || formattedPhone === "Not Available") return "";
        return formattedPhone.replace(/\D/g, '');
    };

    // Contact Info Items
    const contactItems = [
        {
            icon: MapPin,
            title: "Address",
            value: addressLines,
            action: null,
            isClickable: false,
            color: "text-emerald-400"
        },
        {
            icon: Phone,
            title: "Phone",
            value: contactPhone,
            action: () => {
                const cleanPhone = getCleanPhoneDigits(contactPhone);
                if (cleanPhone) window.location.href = `tel:${cleanPhone}`;
            },
            isClickable: contactPhone && contactPhone !== "Not Available",
            color: "text-blue-400"
        },
        {
            icon: Mail,
            title: "Email",
            value: contactEmail,
            action: () => window.location.href = `mailto:${contactEmail}`,
            isClickable: contactEmail && contactEmail !== "Not Available",
            color: "text-purple-400"
        },
    ];

    // Main navigation links
    const mainNavigationLinks = [
        { name: "Home", href: "/" },
        { name: "Contact Us", href: "/contact" },
        { name: "About Us", href: "/our-features" }
    ];

    return (
        <footer
            className={`bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-t border-slate-700/50 relative overflow-hidden${
                isNativeMobileApp() && !isKiosk ? ' fx-m-hide' : ''
            }`}
            style={{ fontFamily: "var(--flex-font, 'Inter', sans-serif)" }}
        >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-600/10 via-transparent to-transparent" />
            <div
                className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"
                style={{ background: 'radial-gradient(circle, var(--flex-accent-soft, rgba(20, 184, 166, 0.14)) 0%, transparent 70%)' }}
            />
            <div
                className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"
                style={{ background: 'radial-gradient(circle, var(--flex-primary-soft, rgba(37, 99, 235, 0.16)) 0%, transparent 70%)' }}
            />

            <div className="relative mx-auto w-full max-w-7xl px-6 py-16 lg:py-20">
                {/* Main Footer Content */}
                <div className={`grid grid-cols-1 gap-10 mb-12 ${isKiosk ? '' : 'lg:grid-cols-4'}`}>
                    {!isKiosk && (
                        <>
                            {/* Social Media Section - Redesigned */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <h3 className="flex items-center text-lg font-semibold text-white mb-6 pb-2 border-b border-slate-600">
                                    <Share2 className="w-5 h-5 mr-3 text-blue-400" />
                                    Connect With Us
                                </h3>

                                <SocialIcons />
                            </motion.div>

                            {/* Quick Navigation Links */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.1 }}
                            >
                                <h3 className="flex items-center text-lg font-semibold text-white mb-6 pb-2 border-b border-slate-600">
                                    <Building2 className="w-5 h-5 mr-3 text-blue-400" />
                                    Navigation
                                </h3>
                                <ul className="space-y-3">
                                    {mainNavigationLinks.map((link) => (
                                        <li key={link.name}>
                                            <motion.a
                                                whileHover={{ x: 4 }}
                                                href={link.href}
                                                className="flex items-center text-slate-300 hover:text-blue-400 transition-all duration-200 group text-sm font-medium"
                                            >
                                                <ArrowRight className="w-3 h-3 mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                                {link.name}
                                            </motion.a>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        </>
                    )}

                    {/* Legal Documents / Policies */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.15 }}
                    >
                        <h3 className="flex items-center text-lg font-semibold text-white mb-6 pb-2 border-b border-slate-600">
                            <FileText className="w-5 h-5 mr-3 text-blue-400" />
                            Legal Documents
                        </h3>
                        {quickLinks.length > 0 ? (
                            <ul className="space-y-3">
                                {quickLinks.map((link, index) => (
                                    <li key={index}>
                                        <motion.a
                                            whileHover={{ x: 4 }}
                                            href={link.href}
                                            onClick={(e) => {
                                                if (link.document) {
                                                    handlePolicyClick(link.document, e);
                                                }
                                            }}
                                            className="flex items-center text-slate-300 hover:text-blue-400 transition-all duration-200 group text-sm font-medium"
                                        >
                                            <ArrowRight className="w-3 h-3 mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                            {link.name}
                                        </motion.a>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-slate-400 text-sm italic p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
                                No legal documents available
                            </div>
                        )}
                    </motion.div>

                    {/* Contact Information */}
                    {!isKiosk && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                        >
                            <h3 className="text-lg font-semibold text-white mb-6 pb-2 border-b border-slate-600">
                                Contact Information
                            </h3>
                            <div className="space-y-4">
                                {contactItems.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        className={`flex items-start space-x-3 p-3 rounded-lg transition-all duration-200 ${item.isClickable ? 'hover:bg-slate-800/50 cursor-pointer' : ''
                                            }`}
                                        whileHover={item.isClickable ? { x: 4 } : {}}
                                        onClick={item.isClickable ? item.action : null}
                                    >
                                        <div className={`w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg`}>
                                            <item.icon className={`w-5 h-5 ${item.color}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-slate-200 font-semibold text-sm mb-1">{item.title}</h4>
                                            {item.title === "Address" ? (
                                                <div className="text-slate-300 text-sm space-y-0.5">
                                                    {Array.isArray(item.value) && item.value.length > 0 ? (
                                                        item.value.map((line, idx) => (
                                                            <div key={idx}>{line}</div>
                                                        ))
                                                    ) : (
                                                        <div>{item.value}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-slate-300 text-sm break-words">
                                                    {item.value}
                                                </p>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* Bottom Section */}
                <div className="border-t border-slate-600/50 pt-8">
                    <div className={`flex flex-col items-center space-y-6 lg:space-y-0 ${isKiosk ? 'justify-center' : 'lg:flex-row justify-between'}`}>
                        {/* Copyright */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <p className="text-slate-400 text-sm font-medium">
                                © {new Date().getFullYear()}{" "}
                                <span className="text-slate-200 font-semibold">{company?.name || companyState?.name || "Company Name"}</span>
                                . All Rights Reserved.
                            </p>
                        </motion.div>

                        {/* Powered by Section */}
                        {!isKiosk && (
                            <motion.div
                                className="flex items-center space-x-3 backdrop-blur-sm px-5 py-2.5 border shadow-lg rounded-xl"
                                style={{
                                    background: 'linear-gradient(90deg, var(--flex-primary-soft, rgba(37, 99, 235, 0.16)) 0%, var(--flex-accent-soft, rgba(20, 184, 166, 0.14)) 100%)',
                                    borderColor: 'var(--flex-primary-muted, rgba(37, 99, 235, 0.3))'
                                }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <span className="text-slate-200 text-sm font-medium">Powered by</span>
                                <img
                                    loading="lazy"
                                    src={KRUMAGO.IMAGES?.KRUMAGO_3 || KRUMAGO}
                                    alt="Krumago"
                                    className="h-6 rounded"
                                />
                                <a
                                    href="https://www.krumago.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-300 font-semibold text-sm transition-colors duration-200"
                                >
                                    www.krumago.com
                                </a>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default FlexOfficeFooter;
